import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import RoleSelectionPage from "@/pages/RoleSelectionPage";
import ClientCategoriesPage from "@/pages/ClientCategoriesPage";
import ContractorRegisterPage from "@/pages/ContractorRegisterPage";
import JobPostingPage from "@/pages/JobPostingPage";
import DashboardRouter, { ClientDashboard, ContractorDashboard } from "@/pages/DashboardPage";
import JobDetailPage from "@/pages/JobDetailPage";
import AdminPage from "@/pages/AdminPage";
import ContractorSearchPage from "@/pages/ContractorSearchPage";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResultPages";

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/role" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (!user.role) return <Navigate to="/role" replace />;
  if (user.role === "contractor" && !user.contractor_profile)
    return <Navigate to="/contractor/register" replace />;
  return <Navigate to="/dashboard" replace />;
};

const AppRoutes = () => {
  // Synchronously check the URL hash for a Google OAuth session_id — must run
  // before ProtectedRoute so we intercept the callback BEFORE any /auth/me call.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/role" element={<RoleSelectionPage />} />
      <Route path="/contractors" element={<ContractorSearchPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />

      <Route
        path="/client/categories"
        element={
          <ProtectedRoute>
            <ClientCategoriesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/job/new"
        element={
          <ProtectedRoute>
            <JobPostingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/dashboard"
        element={
          <ProtectedRoute>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/contractor/register"
        element={
          <ProtectedRoute>
            <ContractorRegisterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contractor/dashboard"
        element={
          <ProtectedRoute>
            <ContractorDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />

      <Route
        path="/jobs/:jobId"
        element={
          <ProtectedRoute>
            <JobDetailPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => (
  <div className="App">
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  </div>
);

export default App;
